import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass, CircleNotch, DotsThreeVertical, PaperPlaneTilt, Prohibit, ArrowCounterClockwise,
  Eye, PencilSimple, SignOut, EnvelopeSimple,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import InvitationLinkDialog from '../shared/InvitationLinkDialog';
import UserAdminDialog from '../UserAdminDialog';
import DepartCollaborateur from './DepartCollaborateur';
import { BOUTONS, STATUT_COMPTE, libelleRole } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COULEUR_STATUT = {
  actif: 'bg-[#E8F5E9] text-[#2D6A4F]',
  invite: 'bg-[#E3F2FD] text-[#1E3A5F]',
  invitation_expiree: 'bg-[#FDECEA] text-[#8B3A3A]',
  desactive: 'bg-[#F0F0EC] text-[#6B6B6B]',
};

function dateCourte(s) {
  if (!s) return 'Jamais';
  try { return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; }
}

export function BadgeStatut({ statut }) {
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 whitespace-nowrap ${COULEUR_STATUT[statut] || COULEUR_STATUT.actif}`}
      data-testid={`statut-${statut}`}>
      {STATUT_COMPTE[statut] || STATUT_COMPTE.actif}
    </span>
  );
}

/**
 * RP84-86 — Liste des comptes d'un onglet de « Équipe & accès » (Cabinet, Partenaires,
 * Entreprises, Clients) : statut (Actif, Invité pas encore activé, Invitation expirée,
 * Désactivé), renvoyer l'invitation, désactiver / réactiver (plus de suppression ici :
 * elle est dans la fiche › Avancé, pour un compte sans historique), gérer le départ
 * d'un collaborateur (RP93-95).
 * Props : groupe, onModifier? (compte) → ouvre la fiche d'édition, rechargement? (compteur).
 */
export default function ComptesListe({ groupe, onModifier, rechargement = 0 }) {
  const { user: moi, isAdmin } = useAuth();
  const monId = moi?._id || moi?.id;
  const [comptes, setComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [q, setQ] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [selection, setSelection] = useState(new Set());
  const [envoi, setEnvoi] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [aDesactiver, setADesactiver] = useState(null);
  const [depart, setDepart] = useState(null);
  const [lienInvitation, setLienInvitation] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/equipe-acces/comptes`, { params: { groupe }, withCredentials: true });
      setComptes(data || []);
    } catch {
      toast.error('Impossible de charger les comptes');
    } finally {
      setChargement(false);
    }
  }, [groupe]);

  useEffect(() => { charger(); }, [charger, rechargement]);

  const filtres = useMemo(() => comptes.filter((c) => {
    if (filtreStatut && c.statut !== filtreStatut) return false;
    if (q) {
      const s = q.toLowerCase();
      if (![c.name, c.email, c.entreprise_nom].some((v) => (v || '').toLowerCase().includes(s))) return false;
    }
    return true;
  }), [comptes, q, filtreStatut]);

  const compteurs = useMemo(() => comptes.reduce((acc, c) => ({ ...acc, [c.statut]: (acc[c.statut] || 0) + 1 }), {}), [comptes]);
  const invitables = (c) => c.statut === 'invite' || c.statut === 'invitation_expiree';

  const renvoyer = async (c) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/users/${c.id}/resend-invitation`, {}, { withCredentials: true });
      if (data?.email_sent) toast.success(data.message || `Invitation renvoyée à ${c.email}`);
      else if (data?.activation_url) setLienInvitation(data);
      else toast.info(data?.message || "L'e-mail n'est pas parti");
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de renvoyer l'invitation");
    }
  };

  const desactiver = async () => {
    const c = aDesactiver;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/users/${c.id}/desactiver`, {}, { withCredentials: true });
      toast.success(data.message || 'Compte désactivé');
      setADesactiver(null);
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de désactiver le compte');
    }
  };

  const reactiver = async (c) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/users/${c.id}/reactiver`, {}, { withCredentials: true });
      toast.success(data.message || 'Compte réactivé');
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de réactiver le compte');
    }
  };

  const envoyerInvitations = async () => {
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/send-invitations`, { user_ids: Array.from(selection) }, { withCredentials: true });
      toast.success(data.message);
      setSelection(new Set());
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'envoyer les invitations");
    } finally {
      setEnvoi(false);
    }
  };

  const basculerSelection = (id) => setSelection((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div className="space-y-3" data-testid={`comptes-liste-${groupe}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom, un e-mail, une entreprise…"
            className="pl-9 rounded-none" data-testid={`comptes-recherche-${groupe}`} />
        </div>
        <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}
          className="border border-[#E0E0DB] px-3 py-2 text-sm bg-white" data-testid={`comptes-filtre-statut-${groupe}`}>
          <option value="">Tous les statuts ({comptes.length})</option>
          {Object.entries(STATUT_COMPTE).map(([k, v]) => <option key={k} value={k}>{v} ({compteurs[k] || 0})</option>)}
        </select>
      </div>

      <div className="border border-[#E0E0DB] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F3] border-b border-[#E0E0DB]">
            <tr className="text-left">
              {isAdmin && <th className="px-3 py-2 w-8" aria-label="Sélection" />}
              <th className="px-3 py-2 font-semibold">Nom</th>
              <th className="px-3 py-2 font-semibold hidden md:table-cell">Rôle</th>
              <th className="px-3 py-2 font-semibold">Statut</th>
              <th className="px-3 py-2 font-semibold text-center hidden sm:table-cell">Chantiers</th>
              <th className="px-3 py-2 font-semibold hidden lg:table-cell">Dernière connexion</th>
              <th className="px-3 py-2 w-10" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {chargement && (
              <tr><td colSpan={7} className="text-center py-8 text-[#9A9A9A]"><CircleNotch size={20} className="animate-spin inline-block mr-2" />Chargement…</td></tr>
            )}
            {!chargement && filtres.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[#9A9A9A]">{comptes.length ? 'Aucun résultat pour ce filtre' : 'Aucun compte pour le moment'}</td></tr>
            )}
            {!chargement && filtres.map((c) => {
              const estMoi = c.id === monId;
              return (
                <tr key={c.id} className={`border-b border-[#F0F0EC] hover:bg-[#FAFAF8] ${c.statut === 'desactive' ? 'text-[#9A9A9A]' : ''}`}
                  data-testid={`compte-${c.id}`}>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      {invitables(c) && (
                        <input type="checkbox" checked={selection.has(c.id)} onChange={() => basculerSelection(c.id)}
                          aria-label={`Sélectionner ${c.name}`} />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <button type="button" className="text-left" onClick={() => isAdmin && setFiche(c.id)}>
                      <span className="font-medium text-[#2C2C2C] block">{c.name || '—'}{estMoi && <span className="text-xs text-[#9A9A9A]"> (vous)</span>}</span>
                      <span className="text-xs text-[#6B6B6B] block break-all">{c.email}</span>
                      {c.entreprise_nom && <span className="text-xs text-[#9A9A9A] block">{c.entreprise_nom}</span>}
                      <span className="text-xs text-[#6B6B6B] md:hidden">{libelleRole(c.role)}</span>
                    </button>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    {libelleRole(c.role)}
                    {c.acces_restreint && <span className="block text-xs text-[#6B6B6B]">Accès restreint</span>}
                    {c.role === 'admin' && c.is_associe === false && <span className="block text-xs text-[#6B6B6B]">Sans accès aux chantiers</span>}
                  </td>
                  <td className="px-3 py-2"><BadgeStatut statut={c.statut} /></td>
                  <td className="px-3 py-2 text-center hidden sm:table-cell">{c.chantiers_count || '—'}</td>
                  <td className="px-3 py-2 text-xs text-[#6B6B6B] hidden lg:table-cell">{dateCourte(c.last_login_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" className="w-8 h-8 inline-flex items-center justify-center border border-[#E0E0DB] bg-white hover:border-[#C4773B]"
                            aria-label={`Actions pour ${c.name}`} data-testid={`compte-actions-${c.id}`}>
                            <DotsThreeVertical size={16} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none">
                          <DropdownMenuItem onClick={() => setFiche(c.id)}><Eye size={14} className="mr-2" />Voir la fiche</DropdownMenuItem>
                          {onModifier && (
                            <DropdownMenuItem onClick={() => onModifier(c)} data-testid={`compte-modifier-${c.id}`}>
                              <PencilSimple size={14} className="mr-2" />Modifier
                            </DropdownMenuItem>
                          )}
                          {c.statut !== 'desactive' && (
                            <DropdownMenuItem onClick={() => renvoyer(c)} data-testid={`resend-invitation-${c.id}`}>
                              <PaperPlaneTilt size={14} className="mr-2" />{BOUTONS.renvoyerInvitation}
                            </DropdownMenuItem>
                          )}
                          {!estMoi && <DropdownMenuSeparator />}
                          {!estMoi && groupe === 'cabinet' && c.statut !== 'desactive' && (
                            <DropdownMenuItem onClick={() => setDepart(c)} data-testid={`compte-depart-${c.id}`}>
                              <SignOut size={14} className="mr-2" />{BOUTONS.gererDepart}
                            </DropdownMenuItem>
                          )}
                          {!estMoi && c.statut !== 'desactive' && (
                            <DropdownMenuItem onClick={() => setADesactiver(c)} className="text-[#8B3A3A]" data-testid={`compte-desactiver-${c.id}`}>
                              <Prohibit size={14} className="mr-2" />{BOUTONS.desactiverCompte}
                            </DropdownMenuItem>
                          )}
                          {!estMoi && c.statut === 'desactive' && (
                            <DropdownMenuItem onClick={() => reactiver(c)} data-testid={`compte-reactiver-${c.id}`}>
                              <ArrowCounterClockwise size={14} className="mr-2" />{BOUTONS.reactiverCompte}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isAdmin && selection.size > 0 && (
        <div className="sticky bottom-4 z-40 bg-white border-2 border-[#C4773B] shadow-lg p-3 flex flex-wrap items-center gap-3" data-testid="bulk-actions-bar">
          <span className="font-semibold text-sm">{selection.size} sélectionné(s)</span>
          <Button onClick={envoyerInvitations} disabled={envoi} className="bg-[#C4773B] hover:bg-[#A5623A] rounded-none gap-2" data-testid="send-invitations-btn">
            <EnvelopeSimple size={16} weight="bold" /> Envoyer les invitations ({selection.size})
          </Button>
          <Button variant="outline" onClick={() => setSelection(new Set())} className="rounded-none">Tout désélectionner</Button>
        </div>
      )}

      {/* RP86 — confirmation : ce que change la désactivation */}
      <Dialog open={!!aDesactiver} onOpenChange={(o) => !o && setADesactiver(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-desactiver-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>{BOUTONS.desactiverCompte}</DialogTitle>
            <DialogDescription>{aDesactiver?.name} ({aDesactiver?.email})</DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-[#4A4A4A] list-disc ml-5 space-y-1">
            <li>La personne ne peut plus se connecter ; ses liens d'accès et d'invitation ne fonctionnent plus.</li>
            <li>Tout son historique est conservé (comptes rendus, actions, équipes des chantiers).</li>
            <li>Vous pourrez réactiver le compte à tout moment.</li>
            {groupe === 'cabinet' && <li>Pour transmettre ses chantiers, ses actions et ses réunions, utilisez plutôt « {BOUTONS.gererDepart} ».</li>}
          </ul>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setADesactiver(null)} className="rounded-none">Annuler</Button>
            <Button onClick={desactiver} className="bg-[#8B3A3A] hover:bg-[#6E2E2E] rounded-none" data-testid="confirm-desactiver-btn">
              {BOUTONS.desactiverCompte}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {fiche && <UserAdminDialog userId={fiche} onClose={() => setFiche(null)} onChanged={charger} />}
      {depart && <DepartCollaborateur compte={depart} onClose={() => setDepart(null)} onTermine={() => { setDepart(null); charger(); }} />}
      <InvitationLinkDialog invitation={lienInvitation} onClose={() => setLienInvitation(null)} />
    </div>
  );
}
